import React from 'react';
import styled from 'styled-components';
import Head from 'next/head';
import { RichText } from 'prismic-reactjs';
import { getPrismicApi } from 'shared/utils/prismic';
import { getDocumentByType } from 'shared/utils/documents';
import { FlexColumn, Navbar, Footer, flexCenter, FlexCenter } from 'shared/components';
import { upToMedia } from 'shared/style/media';
import getCookies from '../utils/cookies';
import media from '../style/media';

const Wrapper = styled(FlexColumn)`
  min-height: 100vh;
`;

const Banner = styled(FlexCenter)`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 5001;
  height: 35px;
  width: 100%;
  background-color: ${({ background }) => background};
  color: ${({ color }) => color};
  cursor: pointer;
  justify-content: space-between;
  ${media.tablet`
    justify-content: center;
    height: 60px;
  `}
`;

const BannerText = styled.div`
  display: none;
  ${({ image }) => image && image.url && 'margin: 0 50px 0 0;'}
  padding: 0;
  font-size: 18px;
  ${media.tablet`
    display: block;
  `}
`;

const BannerTextNondinamic = styled.div`
  display: none;
  margin: 0 25px;
  padding: 0;
  font-size: 18px;
  ${media.tablet`
    display: block;
  `}
`;

const BannerTextMobile = styled.div`
  margin: 0 14px 0 0;
  padding: 0;
  text-align: center;
  flex-grow: 1;
  font-size: 12px;
  ${media.tablet`
    display: none; 
  `}
`;

const BannerButton = styled.div`
  margin: 0;
  padding: 0;
  background: black;
  color: white;
  width: 70px;
  height: 16px;
  font-size: 9px;
  ${flexCenter};
  ${media.tablet`
    width: 120px;
    height: 32px;
    font-size: 16px;
  `}
`;

const BannerButtonArrow = styled.span`
  padding-top: 1px;
  padding-left: 4px;
  ${media.tablet`
    padding-top: 3px;
  `}
`;

const BannerImage = styled.img`
  margin: 0;
  padding: 0;
  height: 35px;
  ${media.tablet`
    height: 60px;
  `}
`;

const Content = styled(FlexColumn)`
  margin-top: ${({ theme }) => theme.constants.navbarHeight}rem;
  flex: 1 1 auto;

  ${upToMedia.mobile`
  position: absolute;
  top: 0;
  right: 0;
  left:0;
  width: 100%;
  min-height: 100vh;
  z-index: 1;
  margin-top: 8rem;
  `}
`;

let cacheNavbarAndFooter = {};

export default mapDocumentsToProps => WrappedComponent => {
  class PageWithProps extends React.Component {
    static async getInitialProps(ctx) {
      const {
        lang,
        pageId,
        languages,
        ctx: { req },
        query,
      } = ctx;

      const api = await getPrismicApi();
      const cookies = getCookies(ctx);

      let expirementRef;
      let previewRef;
      if (cookies) {
        expirementRef = api.experiments.refFromCookie(cookies['io.prismic.experiment']);
        previewRef = cookies['io.prismic.preview'];
      }
      const ref = previewRef || expirementRef || null;

      if (!cacheNavbarAndFooter[lang]) {
        cacheNavbarAndFooter = {
          ...cacheNavbarAndFooter,
          [lang]: {
            navbarData: await getDocumentByType('navbar', lang, ref),
            footerData: await getDocumentByType('footer', lang, ref),
            cookiePopupData: await getDocumentByType('cookies_popup', lang, ref),
          },
        };
      }

      const pageData = await mapDocumentsToProps({
        lang,
        pageId,
        ref,
        ip: req && req.ip,
        query,
        cookies: req && req.cookies,
      });

      const { navbarData, footerData, cookiePopupData } = cacheNavbarAndFooter[lang];
      return {
        pageData,
        navbarData: { ...navbarData, languages },
        footerData,
        cookiePopupData,
      };
    }

    render() {
      const {
        pageData,
        pageData: {
          meta_title,
          meta_description,
          meta_keywords,
          utm_source,
          utm_medium,
          canonical_href,
          banner_toggle,
          banner_text,
          banner_text_mobile,
          banner_image,
          banner_url,
          banner_background_color,
          banner_text_color,
        },
        navbarData,
        footerData,
      } = this.props;
      const isStaticBanner = canonical_href === 'http://harmon.ie' && !banner_toggle;
      const isBanner = canonical_href === 'http://harmon.ie' && banner_toggle;

      return (
        <>
          <Head>
            <title>{meta_title || 'harmon.ie'}</title>
            {meta_description && <meta name="description" content={meta_description} />}
            {meta_keywords && <meta name="keywords" content={meta_keywords} />}
            <link rel="shortcut icon" href="/favicon.ico" />
            {canonical_href && <link rel="canonical" href={canonical_href} />}
          </Head>

          <Wrapper>
            {isBanner && (
              <Banner
                background={banner_background_color}
                color={banner_text_color}
                onClick={() => banner_url && banner_url.url && window.open(banner_url.url)}
              >
                <BannerTextMobile>
                  {(banner_text_mobile && RichText.render(banner_text_mobile)) ||
                    (banner_text && RichText.render(banner_text))}
                </BannerTextMobile>
                <BannerText image={banner_image}>
                  {banner_text && RichText.render(banner_text)}
                </BannerText>
                {banner_image && banner_image.url && <BannerImage src={banner_image.url} />}
              </Banner>
            )}

            {isStaticBanner && (
              <Banner
                background="#0178D4"
                color="#ffffff"
                onClick={() =>
                  window.open('https://intrazone.libsyn.com/drag-and-drop-outlook-to-sharepoint')
                }
              >
                <BannerTextNondinamic>Microsoft partner harmon.ie</BannerTextNondinamic>
                <BannerTextMobile>harmon.ie stars on</BannerTextMobile>
                <BannerImage src="/images/microsoft_harmoni_logo.png" />
                <BannerTextNondinamic>
                  helps Bosch manage millions of daily emails
                </BannerTextNondinamic>
                <BannerButton>
                  Listen now <BannerButtonArrow>{'>'}</BannerButtonArrow>
                </BannerButton>
              </Banner>
            )}

            <Navbar {...{ ...navbarData, utm_source, utm_medium, isBanner, isStaticBanner }} />

            <Content>
              <div className="unreset">
                <WrappedComponent {...pageData} />
              </div>

              <Footer {...{ ...footerData, utm_source, utm_medium }} />
            </Content>
          </Wrapper>
        </>
      );
    }
  }

  return PageWithProps;
};
